import React, { useState } from 'react';
import { ArrowRightIcon } from 'lucide-react';
import { EMAIL_ENDPOINT } from '../data/links';
import { TOTAL_PROJECTS } from '../data/books';
interface GateProps {
  onUnlock: (email: string) => void;
}
export function record(email: string, source: string, extra?: string) {
  if (!EMAIL_ENDPOINT) return;
  try {
    fetch(EMAIL_ENDPOINT, {
      method: 'POST',
      mode: 'no-cors',
      body: JSON.stringify({
        email,
        source,
        extra,
        at: new Date().toISOString()
      })
    }).catch(() => {});
  } catch {
    /* recording an email must never block the customer */
  }
}

/* One field. No PIN, no code, no password. Any address gets in.
   The email is here to reach the customer later, not to check them at the
   door: a door that can reject a paying customer is worse than an open one. */
export function Gate({
  onUnlock
}: GateProps) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const cleanEmail = email.trim();
    if (!cleanEmail) {
      setError('We need your email to let you in and send you the new packs.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
      setError('That email address does not look right.');
      return;
    }
    setError(null);
    record(cleanEmail, 'gate');
    onUnlock(cleanEmail);
  };
  const fieldClass = 'w-full border-b bg-transparent px-0 py-2.5 font-sans text-[17px] text-ink placeholder:text-ink/35 transition-colors duration-150 ease-out focus:outline-none';
  return <main className="blueprint flex min-h-screen w-full flex-col justify-between bg-ground px-6 py-10 sm:px-10 sm:py-14" data-id="element-140" data-mp="35irymtywjr12">
      <header data-id="element-141" data-mp="30ljguuho1p0c">
        <div className="flex items-baseline gap-3" data-id="element-142" data-mp="30ixb90ec5jkr">
          <span className="font-display text-[17px] font-semibold text-ink" data-id="element-143" data-mp="1b6v4gt92s72b">Little Engineer</span>
          <span aria-hidden="true" className="h-3 w-px bg-rule" data-id="element-144" data-mp="167qz6dp9uth9" />
          <span className="smallcaps font-sans text-[10px] text-gold" data-id="element-145" data-mp="3698c0m2yoq7t">Mega Kit</span>
        </div>
      </header>

      <div className="brackets mx-auto w-full max-w-[30rem] border border-rule bg-surface p-8 sm:p-10" data-id="element-146" data-mp="3b7jnf18wn8s5">
        <p className="smallcaps font-sans text-[10px] font-semibold text-gold" data-id="element-147" data-mp="3f9dk6yfzbsn1">Your access</p>
        <h1 className="mt-3 font-display font-semibold text-[38px] leading-[1.05] text-ink sm:text-[46px]" data-id="element-148" data-mp="3k99do3r1w1oc">
          The Workshop
        </h1>
        <p className="mt-4 max-w-measure font-sans text-[16px] leading-relaxed text-ink/65" data-id="element-149" data-mp="3p7kmcb5m064z">
          Twelve books, {TOTAL_PROJECTS} projects, and a search that finds the right one in about
          four seconds. Type the email you ordered with and go straight in.
        </p>

        <form onSubmit={handleSubmit} noValidate className="mt-11" data-id="element-150" data-mp="6sjt9bcdb41v">
          <div data-id="element-151" data-mp="btg8gl266cml">
            <label htmlFor="email" className="smallcaps block font-sans text-[10px] text-ink/55" data-id="element-152" data-mp="btppvk95fbgo">
              Email address
            </label>
            <input id="email" name="email" type="email" value={email} autoComplete="email" autoFocus placeholder="you@example.com" aria-invalid={Boolean(error)} aria-describedby={error ? 'email-error' : 'email-hint'} onChange={event => {
            setEmail(event.target.value);
            if (error) setError(null);
          }} className={`${fieldClass} mt-2 ${error ? 'border-[#E0806B] focus:border-[#E0806B]' : 'border-rule focus:border-ink'}`} data-id="element-153" data-mp="gpwxril3ux19" />
            {error ? <p id="email-error" role="alert" className="mt-2 font-sans text-[12px] leading-relaxed text-[#E0806B]" data-id="element-154" data-mp="zutd7ac7j14n">
                {error}
              </p> : <p id="email-hint" className="mt-2 font-sans text-[12px] text-ink/40" data-id="element-155" data-mp="1nmgud0jo2xtq">
                No password and no code. We use it to send you the new packs, nothing else.
              </p>}
          </div>

          <button type="submit" className="mt-10 inline-flex w-full items-center justify-between gap-3 bg-green px-5 py-3.5 font-sans text-[13px] text-onaccent transition-colors duration-150 ease-out hover:bg-[#33684D] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ink focus-visible:ring-offset-2 focus-visible:ring-offset-ground sm:w-auto" data-id="element-156" data-mp="1d4bnd4maurcv">
            <span data-id="element-157" data-mp="1vsqd7okvrzc3">Enter The Workshop</span>
            <ArrowRightIcon aria-hidden="true" className="h-4 w-4" strokeWidth={1.5} data-id="element-158" data-mp="1vvw1j1loqx6r" />
          </button>
        </form>
      </div>

      <footer className="border-t border-rule pt-5" data-id="element-159" data-mp="1m3qabu39cjsm">
        <p className="font-sans text-[13px] text-ink/45" data-id="element-160" data-mp="1m25jofk2dl01">
          Trouble getting in? Reply to your order email and we will help.
        </p>
      </footer>
    </main>;
}